import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useShallow } from "zustand/react/shallow";
import { Copy, Trash2, Bookmark, Lock, Unlock, Settings2, GripVertical, ChevronDown } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ColorPicker } from "@/components/ui/ColorPicker";
import { FibLevelEditor } from "@/components/lytick/FibLevelEditor";
import {
  duplicateDrawing,
  getDrawingSnapshot,
  getDrawingViewportBounds,
  removeDrawing,
  setDrawingProperties,
  setDrawingLocked,
  setDrawingExtendData,
} from "@/lib/lytick/overlay-bridge";
import { useDrawingStore } from "@/stores/drawingStore";

export function ObjectInspector() {
  const { drawings, selectedDrawingId, selectedDrawingIds } = useDrawingStore(
    useShallow((state) => ({
      drawings: state.drawings,
      selectedDrawingId: state.selectedDrawingId,
      selectedDrawingIds: state.selectedDrawingIds,
    })),
  );
  
  const selectedDrawing = useMemo(
    () => drawings.find((drawing) => drawing.id === selectedDrawingId) ?? null,
    [drawings, selectedDrawingId],
  );
  
  const [snapshotVersion, setSnapshotVersion] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  
  // Position tracks right above/below the drawing bounds.
  const [desktopPosition, setDesktopPosition] = useState({ left: 0, top: 0, opacity: 0 });
  const [showFibSettings, setShowFibSettings] = useState(false);

  useEffect(() => {
    setShowFibSettings(false);
  }, [selectedDrawingId]);

  useEffect(() => {
    setSnapshotVersion((value) => value + 1);
  }, [selectedDrawingId, selectedDrawingIds.length, drawings.length]);

  useEffect(() => {
    if (!selectedDrawingId || selectedDrawingIds.length !== 1) {
      return;
    }

    let frameId = 0;
    const updatePosition = () => {
      const bounds = getDrawingViewportBounds(selectedDrawingId);
      const container = cardRef.current?.offsetParent as HTMLElement | null;
      const cardWidth = cardRef.current?.offsetWidth ?? 300;
      const cardHeight = cardRef.current?.offsetHeight ?? 44;
      const containerWidth = container?.clientWidth ?? window.innerWidth;

      if (bounds) {
        // Center horizontally on the drawing's bounding box
        let nextLeft = bounds.left + (bounds.right - bounds.left) / 2 - cardWidth / 2;
        // Clamp to screen edges
        nextLeft = Math.max(12, Math.min(containerWidth - cardWidth - 12, nextLeft));

        // Place 16px above the drawing, or 16px below if too close to top
        let nextTop = bounds.top - cardHeight - 16;
        if (nextTop < 12) {
          nextTop = bounds.bottom + 16;
        }

        setDesktopPosition((current) =>
          Math.abs(current.left - nextLeft) > 1 || Math.abs(current.top - nextTop) > 1
            ? { left: nextLeft, top: nextTop, opacity: 1 }
            : { ...current, opacity: 1 },
        );
      }

      frameId = window.requestAnimationFrame(updatePosition);
    };

    // Need a tiny delay for the card to measure its own width first
    setTimeout(() => {
      frameId = window.requestAnimationFrame(updatePosition);
    }, 10);
    
    return () => window.cancelAnimationFrame(frameId);
  }, [selectedDrawingId, selectedDrawingIds.length]);

  // Esc to dismiss inspector
  useEffect(() => {
    if (!selectedDrawingId) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        useDrawingStore.getState().clearDrawingSelection();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedDrawingId]);

  const snapshot = selectedDrawingId ? getDrawingSnapshot(selectedDrawingId) : null;

  if (
    !selectedDrawing ||
    !selectedDrawingId ||
    !snapshot ||
    selectedDrawingIds.length > 1
  ) {
    return null;
  }

  const extendData = (snapshot.extendData || {}) as any;
  const isText = selectedDrawing.toolId === "textAnnotation" || selectedDrawing.toolId === "textTag";
  const supportsVisualStyle = selectedDrawing.toolId !== "riskReward" && !isText;
  const hasSettings = selectedDrawing.toolId === "fibonacci";

  const handleSaveAsDefault = () => {
    if (!selectedDrawingId || !snapshot) return;
    const store = useDrawingStore.getState();
    store.saveTemplate(selectedDrawing.toolId, {
      color: snapshot.color,
      lineWidth: snapshot.lineWidth ?? 2,
      lineStyle: snapshot.lineStyle ?? "solid",
      opacity: snapshot.opacity ?? 100,
    });
    toast.success("Saved as default", {
      description: "New drawings will use these styles.",
    });
  };

  /**
   * Separator line inside the toolbar
   */
  const ToolbarSeparator = () => (
    <div className="h-5 w-px bg-border/60 mx-1 flex-shrink-0" />
  );

  /**
   * Reusable toolbar icon button
   */
  const ToolbarButton = ({ 
    icon: Icon, 
    onClick, 
    tooltip, 
    active,
    danger
  }: { 
    icon: React.ElementType, 
    onClick: () => void, 
    tooltip?: string,
    active?: boolean,
    danger?: boolean
  }) => (
    <button
      type="button"
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        "flex h-8 w-8 items-center justify-center rounded-md transition-colors",
        active ? "bg-surface-active text-foreground" : 
        danger ? "text-muted-foreground hover:bg-loss/10 hover:text-loss" :
        "text-muted-foreground hover:bg-surface-hover hover:text-foreground"
      )}
      title={tooltip}
      aria-label={tooltip}
    >
      <Icon className="size-4" />
    </button>
  );

  return (
    <>
      <div
      ref={cardRef}
      className="absolute z-20 flex h-[44px] items-center gap-1 rounded-xl border border-border/80 bg-card/95 px-1.5 shadow-xl backdrop-blur-md transition-opacity duration-200"
      style={{ 
        left: desktopPosition.left, 
        top: desktopPosition.top,
        opacity: desktopPosition.opacity,
        pointerEvents: desktopPosition.opacity ? "auto" : "none"
      }}
      onPointerDown={(e) => {
        // Prevent clicking the toolbar from deselecting the drawing on the chart canvas
        e.stopPropagation();
      }}
      onPointerUp={(e) => {
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {/* Drag handle (visual only for now, tracks drawing) */}
      <div className="flex h-8 w-6 cursor-grab items-center justify-center text-muted-foreground/40 hover:text-muted-foreground">
        <GripVertical className="size-4" />
      </div>

      {isText && (
        <>
          {/* Edit text button */}
          <ToolbarButton
            icon={Settings2}
            onClick={() => useDrawingStore.getState().setEditingTextDrawingId(selectedDrawingId)}
            tooltip="Edit Text"
          />

          <ToolbarSeparator />

          {/* Color Picker Popover */}
          <Popover>
            <PopoverTrigger asChild>
              <button 
                type="button"
                className="flex items-center justify-center h-8 w-8 rounded-md hover:bg-surface-hover transition-colors"
                title="Text Color"
              >
                <div 
                  className="w-4 h-4 rounded-sm border border-border" 
                  style={{ backgroundColor: extendData.color || "#F9FAFB" }}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" sideOffset={8} align="center" className="w-[240px] p-3">
              <ColorPicker
                value={extendData.color || "#F9FAFB"}
                onChange={(color) => {
                  setDrawingExtendData(selectedDrawingId, {
                    ...extendData,
                    color,
                  });
                  setSnapshotVersion((v) => v + 1);
                }}
                showOpacity={false}
              />
            </PopoverContent>
          </Popover>

          {/* Font Size Popover */}
          <Popover>
            <PopoverTrigger asChild>
              <button 
                type="button"
                className="flex items-center justify-center h-8 px-2 gap-0.5 rounded-md text-muted-foreground hover:bg-surface-hover hover:text-foreground transition-colors"
                title="Font Size"
              >
                <span className="text-xs font-semibold">{extendData.size || "M"}</span>
                <ChevronDown className="size-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" sideOffset={8} align="center" className="w-auto p-1.5 flex flex-col gap-0.5">
              {(["S", "M", "L", "XL"] as const).map((sz) => (
                <button
                  key={sz}
                  onClick={() => {
                    setDrawingExtendData(selectedDrawingId, {
                      ...extendData,
                      size: sz,
                    });
                    setSnapshotVersion((v) => v + 1);
                  }}
                  className={cn(
                    "flex items-center justify-center h-8 w-10 rounded-md text-xs transition-colors",
                    (extendData.size || "M") === sz 
                      ? "bg-golden text-background font-medium" 
                      : "hover:bg-surface-hover"
                  )}
                >
                  {sz}
                </button>
              ))}
            </PopoverContent>
          </Popover>

          {/* Bold button */}
          <button
            type="button"
            onClick={() => {
              setDrawingExtendData(selectedDrawingId, {
                ...extendData,
                bold: !extendData.bold,
              });
              setSnapshotVersion((v) => v + 1);
            }}
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-md font-bold text-xs transition-colors",
              extendData.bold ? "bg-surface-active text-foreground" : "text-muted-foreground hover:bg-surface-hover hover:text-foreground"
            )}
            title="Bold"
          >
            B
          </button>

          {/* Italic button */}
          <button
            type="button"
            onClick={() => {
              setDrawingExtendData(selectedDrawingId, {
                ...extendData,
                italic: !extendData.italic,
              });
              setSnapshotVersion((v) => v + 1);
            }}
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-md italic font-serif text-sm transition-colors",
              extendData.italic ? "bg-surface-active text-foreground" : "text-muted-foreground hover:bg-surface-hover hover:text-foreground"
            )}
            title="Italic"
          >
            I
          </button>

          {/* Background toggle */}
          <button
            type="button"
            onClick={() => {
              setDrawingExtendData(selectedDrawingId, {
                ...extendData,
                showBackground: !extendData.showBackground,
              });
              setSnapshotVersion((v) => v + 1);
            }}
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-md text-xs transition-colors",
              extendData.showBackground !== false ? "bg-surface-active text-foreground" : "text-muted-foreground hover:bg-surface-hover hover:text-foreground"
            )}
            title="Toggle Background"
          >
            BG
          </button>

          {/* Tag intent presets */}
          {selectedDrawing.toolId === "textTag" && (
            <>
              <ToolbarSeparator />
              <div className="flex items-center gap-1.5 px-1">
                <button
                  type="button"
                  onClick={() => {
                    setDrawingExtendData(selectedDrawingId, {
                      ...extendData,
                      background: "rgba(34, 197, 94, 0.25)",
                      color: "#22c55e",
                    });
                    setSnapshotVersion((v) => v + 1);
                  }}
                  className="w-4 h-4 rounded-full bg-emerald-500/80 hover:scale-110 transition-transform"
                  title="Long intent"
                />
                <button
                  type="button"
                  onClick={() => {
                    setDrawingExtendData(selectedDrawingId, {
                      ...extendData,
                      background: "rgba(239, 68, 68, 0.25)",
                      color: "#ef4444",
                    });
                    setSnapshotVersion((v) => v + 1);
                  }}
                  className="w-4 h-4 rounded-full bg-red-500/80 hover:scale-110 transition-transform"
                  title="Short intent"
                />
                <button
                  type="button"
                  onClick={() => {
                    setDrawingExtendData(selectedDrawingId, {
                      ...extendData,
                      background: "rgba(148, 163, 184, 0.25)",
                      color: "#94a3b8",
                    });
                    setSnapshotVersion((v) => v + 1);
                  }}
                  className="w-4 h-4 rounded-full bg-slate-400/80 hover:scale-110 transition-transform"
                  title="Note intent"
                />
                <button
                  type="button"
                  onClick={() => {
                    setDrawingExtendData(selectedDrawingId, {
                      ...extendData,
                      background: "rgba(245, 197, 66, 0.25)",
                      color: "#f5c542",
                    });
                    setSnapshotVersion((v) => v + 1);
                  }}
                  className="w-4 h-4 rounded-full bg-yellow-500/80 hover:scale-110 transition-transform"
                  title="Warning intent"
                />
              </div>
            </>
          )}

          <ToolbarSeparator />
        </>
      )}

      {supportsVisualStyle && (
        <>
          {/* Color Picker Popover */}
          <Popover>
            <PopoverTrigger asChild>
              <button 
                type="button"
                className="flex items-center justify-center h-8 w-8 rounded-md hover:bg-surface-hover transition-colors"
                title="Color and Opacity"
              >
                <div 
                  className="w-4 h-4 rounded-sm border border-border" 
                  style={{ backgroundColor: snapshot.color, opacity: (snapshot.opacity ?? 100) / 100 }}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" sideOffset={8} align="center" className="w-[240px] p-3">
              <ColorPicker
                value={snapshot.color}
                onChange={(color) => {
                  setDrawingProperties(selectedDrawingId, { color });
                  setSnapshotVersion((v) => v + 1);
                }}
                opacity={snapshot.opacity ?? 100}
                onOpacityChange={(opacity) => {
                  setDrawingProperties(selectedDrawingId, { opacity });
                  setSnapshotVersion((v) => v + 1);
                }}
              />
            </PopoverContent>
          </Popover>

          {/* Line Width Popover */}
          <Popover>
            <PopoverTrigger asChild>
              <button 
                type="button"
                className="flex items-center justify-center h-8 w-11 gap-0.5 rounded-md text-muted-foreground hover:bg-surface-hover hover:text-foreground transition-colors"
                title="Line Width"
              >
                <span className="text-xs font-medium font-mono">{snapshot.lineWidth ?? 2}px</span>
                <ChevronDown className="size-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" sideOffset={8} align="center" className="w-auto p-1.5 flex flex-col gap-0.5">
              {[1, 2, 3, 4].map((width) => (
                <button
                  key={width}
                  onClick={() => {
                    setDrawingProperties(selectedDrawingId, { lineWidth: width });
                    setSnapshotVersion((v) => v + 1);
                  }}
                  className={cn(
                    "flex items-center h-8 px-3 rounded-md text-xs transition-colors",
                    (snapshot.lineWidth ?? 2) === width 
                      ? "bg-golden text-background font-medium" 
                      : "hover:bg-surface-hover"
                  )}
                >
                  {width}px
                </button>
              ))}
            </PopoverContent>
          </Popover>

          {/* Line Style Popover */}
          <Popover>
            <PopoverTrigger asChild>
              <button 
                type="button"
                className="flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:bg-surface-hover hover:text-foreground transition-colors"
                title="Line Style"
              >
                <div className={cn(
                  "w-4 border-b-2 border-current",
                  (snapshot.lineStyle ?? "solid") === "dashed" && "border-dashed"
                )} />
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" sideOffset={8} align="center" className="w-auto p-1.5 flex flex-col gap-0.5">
              {(["solid", "dashed"] as const).map((style) => (
                <button
                  key={style}
                  onClick={() => {
                    setDrawingProperties(selectedDrawingId, { lineStyle: style });
                    setSnapshotVersion((v) => v + 1);
                  }}
                  className={cn(
                    "flex items-center justify-between h-8 px-3 gap-3 rounded-md text-xs transition-colors capitalize",
                    (snapshot.lineStyle ?? "solid") === style 
                      ? "bg-golden text-background font-medium" 
                      : "hover:bg-surface-hover text-muted-foreground"
                  )}
                >
                  <span>{style}</span>
                  <div className={cn(
                    "w-4 border-b-2",
                    (snapshot.lineStyle ?? "solid") === style ? "border-background" : "border-muted-foreground",
                    style === "dashed" && "border-dashed"
                  )} />
                </button>
              ))}
            </PopoverContent>
          </Popover>

          <ToolbarSeparator />
        </>
      )}

      {/* Settings (if tool supports it, e.g. Fibonacci) */}
      {hasSettings && (
        <ToolbarButton 
          icon={Settings2} 
          onClick={() => setShowFibSettings(!showFibSettings)} 
          tooltip="Settings" 
          active={showFibSettings}
        />
      )}

      <ToolbarButton 
        icon={Bookmark} 
        onClick={handleSaveAsDefault} 
        tooltip="Save as Default" 
      />
      
      <ToolbarSeparator />

      <ToolbarButton 
        icon={selectedDrawing.locked ? Lock : Unlock} 
        onClick={() => setDrawingLocked(selectedDrawingId, !selectedDrawing.locked)} 
        tooltip={selectedDrawing.locked ? "Unlock" : "Lock"} 
        active={selectedDrawing.locked}
      />
      
      <ToolbarButton 
        icon={Copy} 
        onClick={() => duplicateDrawing(selectedDrawingId)} 
        tooltip="Clone" 
      />
      
      <ToolbarSeparator />
      
      <ToolbarButton 
        icon={Trash2} 
        onClick={() => removeDrawing(selectedDrawingId)} 
        tooltip="Remove" 
        danger
      />
    </div>

    {showFibSettings && selectedDrawing?.toolId === "fibonacci" && typeof window !== "undefined" && createPortal(
      <div className="fixed inset-0 z-[100] pointer-events-none">
        <div className="absolute top-[20vh] right-[5vw] pointer-events-auto">
          <FibLevelEditor drawingId={selectedDrawingId} onClose={() => setShowFibSettings(false)} />
        </div>
      </div>,
      document.body
    )}
    </>
  );
}
